import { useMutation } from '@tanstack/react-query';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import { useConnectedConnectors } from '~/features/onboarding/hooks/use-connections';
import type { ConnectedConnectorResponse } from '~/features/onboarding/types/connection.types';
import { extractApiError } from '~/utils/extract-api-error';

import { ANALYTICS_SYNC_SOURCES } from '../constants';
import { startAnalyticsSync } from '../services/analytics-sync.service';
import { useAnalyticsSyncStore, type SyncWorkflowState } from '../store/analytics-sync.store';
import type { AnalyticsSyncDateRange } from '../types/analytics-sync.types';

/**
 * The subsidiary's connected feeding-source connections (GA4 / Google Ads /
 * GMC) that the sync button will target. Only ACTIVE connections are eligible.
 * Drives the dialog's source list and the empty-state guard.
 */
export function useAnalyticsSyncTargets(): {
  targets: ConnectedConnectorResponse[];
  isLoading: boolean;
} {
  const { data, isLoading } = useConnectedConnectors();
  const targets = useMemo(
    () =>
      (data ?? []).filter(
        (c) => ANALYTICS_SYNC_SOURCES.includes(c.code) && c.connectionStatus === 'ACTIVE',
      ),
    [data],
  );
  return { targets, isLoading };
}

interface TriggerArgs {
  experimentId: string;
  experimentName: string;
  /** Connections to sync — passed in from the dialog (already resolved + filtered). */
  targets: ConnectedConnectorResponse[];
  /** Operator-picked window; omitted → server default (single day ending yesterday). */
  dateRange?: AnalyticsSyncDateRange;
}

/**
 * Fires one `POST analytics/sync` per feeding-source connection and registers a
 * session in the global store so the app-level watcher shows a non-blocking
 * progress toast and polls each workflow to completion. Polling itself lives in
 * the watcher (not here) so it survives navigation away from the trigger page.
 */
export function useTriggerAnalyticsSync() {
  const { t } = useTranslation('experiments');
  const startSession = useAnalyticsSyncStore((s) => s.startSession);

  return useMutation({
    mutationFn: async ({ experimentId, targets, dateRange }: TriggerArgs) => {
      if (targets.length === 0) {
        throw new Error(t('detail.syncAnalytics.toast.noConnections'));
      }
      // Map the app (camelCase) window onto the snake_case request body.
      const date_ranges = dateRange
        ? [{ start_date: dateRange.startDate, end_date: dateRange.endDate }]
        : undefined;
      // Fire all triggers in parallel; a single connector failing (e.g. Meta
      // 429 cooldown, missing GA4 property config) must not abort the others.
      const settled = await Promise.allSettled(
        targets.map((tgt) =>
          startAnalyticsSync({
            connection_id: tgt.connectionId,
            ...(date_ranges ? { date_ranges } : {}),
          }),
        ),
      );

      const workflows: SyncWorkflowState[] = [];
      const failed: { connectorCode: string; message: string }[] = [];
      settled.forEach((res, i) => {
        const tgt = targets[i];
        if (!tgt) return;
        if (res.status === 'fulfilled') {
          workflows.push({
            workflowId: res.value.workflowId,
            connectorCode: res.value.connectorCode || tgt.code,
            connectionId: res.value.connectionId || tgt.connectionId,
            status: 'PENDING',
            percentComplete: null,
          });
        } else {
          failed.push({
            connectorCode: tgt.code,
            message: extractApiError(res.reason, t('detail.syncAnalytics.toast.error')),
          });
        }
      });

      // Every trigger failed — surface as a mutation error (onError toast).
      if (workflows.length === 0) {
        throw new Error(failed[0]?.message ?? t('detail.syncAnalytics.toast.error'));
      }
      return { experimentId, workflows, failed };
    },
    onSuccess: ({ experimentId, workflows, failed }, { experimentName }) => {
      startSession({
        id: experimentId,
        experimentId,
        experimentName,
        toastId: `analytics-sync-${experimentId}`,
        workflows,
        startedAt: Date.now(),
      });
      // Some (but not all) connectors failed to start — note them without
      // blocking the ones that did; the progress toast covers the successes.
      if (failed.length > 0) {
        toast.error(t('detail.syncAnalytics.toast.partialFailure', { count: failed.length }));
      }
    },
    onError: (err) => {
      toast.error(extractApiError(err, t('detail.syncAnalytics.toast.error')));
    },
  });
}
